import type { Lang } from "@/domain/i18n";

/**
 * Legal notices (open point O-5 in docs/PRD.md).
 *
 * Two different kinds of text live here, and the difference matters:
 *
 * - The **privacy notice** is written from what the app verifiably does. No
 *   backend, no accounts, no analytics, no third-party requests at runtime, one
 *   localStorage key. That is checkable, so it is stated plainly.
 *
 * - The **Impressum** is NOT written. §5 DDG requires real details about the
 *   operator -- name, address, contact, and for a company the register entry and
 *   VAT ID. Those are facts nobody here knows. Every one is a marked
 *   placeholder. Inventing something plausible would be worse than an obvious
 *   gap: a wrong Impressum reads as compliant while being useless, and it is
 *   exactly the kind of page nobody re-reads once it looks finished.
 *
 * The liability wording (O-5a) is also still missing and needs someone who can
 * carry legal responsibility. It is not drafted here for the same reason.
 */

export const IMPRESSUM_PLACEHOLDER = "[BITTE AUSFÜLLEN]";

export interface LegalSection {
  heading: string;
  body: string[];
}

export interface LegalDoc {
  title: string;
  updated: string;
  sections: LegalSection[];
}

const P = IMPRESSUM_PLACEHOLDER;

/**
 * Impressum. German only, deliberately: §5 DDG applies to a service offered in
 * Germany and the notice is a German legal requirement, so it is not translated.
 */
export const IMPRESSUM: LegalDoc = {
  title: "Impressum",
  updated: "2026-08-01",
  sections: [
    {
      heading: "Angaben gemäß § 5 DDG",
      body: [`${P} (Name bzw. Firma)`, `${P} (Straße und Hausnummer)`, `${P} (PLZ und Ort)`],
    },
    {
      heading: "Vertreten durch",
      body: [`${P} (vertretungsberechtigte Person)`],
    },
    {
      heading: "Kontakt",
      body: [`E-Mail: ${P}`, `Telefon: ${P}`],
    },
    {
      heading: "Registereintrag",
      body: [
        `Registergericht: ${P}`,
        `Registernummer: ${P}`,
        "Entfällt, wenn kein Eintrag besteht.",
      ],
    },
    {
      heading: "Umsatzsteuer-Identifikationsnummer",
      body: [`${P} (§ 27 a UStG)`, "Entfällt, wenn keine erteilt wurde."],
    },
    {
      heading: "Verantwortlich für den Inhalt",
      body: [`${P} (Name und Anschrift wie oben)`],
    },
  ],
};

/**
 * Privacy notice. Every statement below is a fact about the build and can be
 * checked against the source: `domain/store.ts` for the storage key,
 * `design-system/tokens/fonts.css` and `assets/icons/` for self-hosting, and
 * `vite.config.ts` for the service worker.
 */
export const PRIVACY: Record<Lang, LegalDoc> = {
  de: {
    title: "Datenschutz",
    updated: "2026-08-01",
    sections: [
      {
        heading: "Kurz gefasst",
        body: [
          "Carbdial verarbeitet keine personenbezogenen Daten auf einem Server. Alles, was du eingibst, bleibt auf deinem Gerät.",
        ],
      },
      {
        heading: "Was gespeichert wird",
        body: [
          "Gespeicherte Pläne liegen ausschließlich im lokalen Speicher deines Browsers, unter dem Schlüssel „carbdial.plans.v1“. Dazu gehören Sportart, Körpergewicht, Dauer je Etappe und die gewählten Produkte.",
          "Diese Daten werden nicht übertragen. Sie verlassen dein Gerät nicht.",
        ],
      },
      {
        heading: "Was nicht passiert",
        body: [
          "Es gibt keine Benutzerkonten und keine Anmeldung.",
          "Es findet keine Analyse, kein Tracking und kein Profiling statt.",
          "Es werden keine Cookies gesetzt.",
          "Schriften und Symbole werden mit der Anwendung ausgeliefert, nicht von fremden Servern geladen. Beim Benutzen entstehen keine Verbindungen zu Dritten.",
        ],
      },
      {
        heading: "Offline-Nutzung",
        body: [
          "Die Anwendung legt ihre eigenen Dateien im Browser-Cache ab, damit sie ohne Netz funktioniert. Dieser Cache enthält nur Programmdateien, keine Eingaben.",
        ],
      },
      {
        heading: "Server-Protokolle",
        body: [
          "Beim Abruf der Anwendung fallen beim Hosting-Betrieb technisch bedingt Zugriffsdaten an (IP-Adresse, Zeitpunkt, abgerufene Datei). Umfang und Speicherdauer richten sich nach dem Betrieb des Servers.",
        ],
      },
      {
        heading: "Deine Daten löschen",
        body: [
          "Lösche einen Plan in der Anwendung, oder lösche die Websitedaten für diese Seite in den Browsereinstellungen. Danach ist nichts mehr gespeichert.",
        ],
      },
      {
        heading: "Kontakt",
        body: ["Verantwortlich und erreichbar über die Angaben im Impressum."],
      },
    ],
  },
  en: {
    title: "Privacy",
    updated: "2026-08-01",
    sections: [
      {
        heading: "In short",
        body: [
          "Carbdial processes no personal data on a server. Everything you enter stays on your device.",
        ],
      },
      {
        heading: "What is stored",
        body: [
          "Saved plans live only in your browser's local storage, under the key “carbdial.plans.v1”. That covers the discipline, body weight, per-leg durations and the products you picked.",
          "None of it is transmitted. It does not leave your device.",
        ],
      },
      {
        heading: "What does not happen",
        body: [
          "There are no accounts and no sign-in.",
          "There is no analytics, no tracking and no profiling.",
          "No cookies are set.",
          "Fonts and icons ship with the app rather than loading from third-party servers, so using it creates no connections to anyone else.",
        ],
      },
      {
        heading: "Offline use",
        body: [
          "The app stores its own files in the browser cache so it works without a network. That cache holds program files only, never your input.",
        ],
      },
      {
        heading: "Server logs",
        body: [
          "Fetching the app produces the access data any web server records (IP address, time, file requested). Scope and retention are a matter for whoever operates the server.",
        ],
      },
      {
        heading: "Deleting your data",
        body: [
          "Delete a plan in the app, or clear site data for this page in your browser settings. Nothing is kept after that.",
        ],
      },
      {
        heading: "Contact",
        body: ["Reachable via the details in the Impressum."],
      },
    ],
  },
};

export const LEGAL_LABELS: Record<Lang, { impressum: string; privacy: string; close: string }> = {
  de: { impressum: "Impressum", privacy: "Datenschutz", close: "Schließen" },
  en: { impressum: "Impressum", privacy: "Privacy", close: "Close" },
};
